import { useState, useEffect, useRef, useMemo } from "react";
import { Menu, Transition, Disclosure } from "@headlessui/react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Fragment } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Play, X, Settings, Wand2, Upload, Clock, Wind, Smile, Brackets, Heart, Check, Megaphone, Search, Pause, Loader2, Video, Plus, Globe, Lightbulb, Sparkles, RefreshCw, PenLine, Image as ImageIcon, Zap, ChevronsRight, Laugh, SmilePlus, VolumeX, CircleDot } from "lucide-react";
import services from "../../API";
import { useSelector } from "react-redux";
import { Loading } from "../Loader/Loading";
import axios from "axios";
import { toastify } from "../Toast";
import VoiceLibrarySidebar from "./VoiceLibrarySidebar"; // Import the sidebar component
import AdAssetLibraryModal from "../VideoEditor/components/AdAssetLibraryModal"; // Import the sidebar component
import GenerationHistorySidebar from "./GenerationHistorySidebar";
import SpeechWaveformPlayer from "./SpeechWaveformPlayer";
import ScriptHistoryControls from "./ScriptHistoryControls";
import useScriptHistory from "./useScriptHistory";
// Mock data functions (unchanged)



const defaultVoices = [
  { id: "v1", name: "Sarah", gender: "female", description: "Friendly & Natural", previewUrl: "https://zimzee.com/voices/sarah.mp3" },
  { id: "v2", name: "Michael", gender: "male", description: "Professional & Deep", previewUrl: "https://zimzee.com/voices/michael.mp3" },
  { id: "v3", name: "Emily", gender: "female", description: "Soft & Narrative", previewUrl: "https://zimzee.com/voices/emily.mp3" },
  { id: "v4", name: "David", gender: "male", description: "Bold & Energetic", previewUrl: "https://zimzee.com/voices/david.mp3" },
];



const TripleChevronsRight = ({ className = "" }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="m1 5 3 3-3 3" />
    <path d="m6 5 3 3-3 3" />
    <path d="m11 5 3 3-3 3" />
  </svg>
);

const voiceCues = [
  { icon: <Zap className="mr-2 h-4 w-4" />, label: "[fast]", value: "[fast]" },
  { icon: <ChevronsRight className="mr-2 h-4 w-4" />, label: "[faster]", value: "[faster]" },
  { icon: <TripleChevronsRight className="mr-2 h-4 w-5" />, label: "[fastest]", value: "[fastest]" },
  { icon: <Clock className="mr-2 h-4 w-4" />, label: "[slow]", value: "[slow]" },
  { icon: <Pause className="mr-2 h-4 w-4" />, label: "[pause]", value: "[pause]" },
  { icon: <Laugh className="mr-2 h-4 w-4" />, label: "[laugh]", value: "[laugh]" },
  { icon: <Smile className="mr-2 h-4 w-4" />, label: "[chuckle]", value: "[chuckle]" },
  { icon: <SmilePlus className="mr-2 h-4 w-4" />, label: "[giggles]", value: "[giggles]" },
  { icon: <Wind className="mr-2 h-4 w-4" />, label: "[sigh]", value: "[sigh]" },
  { icon: <VolumeX className="mr-2 h-4 w-4" />, label: "[whisper]", value: "[whisper]" },
  { icon: <CircleDot className="mr-2 h-4 w-4" />, label: "[breath]", value: "[breath]" },
  { icon: <Megaphone className="mr-2 h-4 w-4" />, label: "[emphasis]", value: "[emphasis]" },
];

const voiceStabilityOptions = [
  { label: "Expressive", description: ["More emotion, variation,", "and personality."], value: 0 },
  { label: "Balanced", description: ["Natural expression with", "consistent delivery."], value: 0.5 },
  { label: "Consistent", description: ["Steadier tone, pacing,", "and delivery."], value: 1 },
];

const normalizeVoiceStability = (value) => {
  const numericValue = Number(Array.isArray(value) ? value[0] : value);

  if (!Number.isFinite(numericValue)) return 0.5;

  return voiceStabilityOptions.reduce((closest, option) =>
    Math.abs(option.value - numericValue) < Math.abs(closest - numericValue)
      ? option.value
      : closest
    , 0.5);
};

const VoiceStabilityControl = ({ value, onChange }) => {
  const selectedOption = voiceStabilityOptions.find((option) => option.value === value);

  return (
    <div>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-3">
        {voiceStabilityOptions.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              aria-pressed={isSelected}
              className="flex min-w-0 flex-col items-center text-center"
            >
              <span className={`w-full rounded-lg px-0.5 sm:px-2 py-1.5 text-[11px] sm:text-sm font-semibold transition-colors ${isSelected
                ? "bg-gray-900 text-white shadow-sm dark:bg-gray-100 dark:text-gray-900"
                : "text-gray-900 dark:text-gray-100"
                }`}>
                {option.label}
              </span>
              <span className="mt-2 w-full text-[11px] sm:text-xs leading-4 text-gray-500 dark:text-gray-400">
                {option.description.join(" ")}
              </span>
            </button>
          );
        })}
      </div>

      <div className="relative mt-4 h-4">
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div
          className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-orange-500 transition-all"
          style={{ width: `${value * 100}%` }}
        />
        <input
          type="range"
          min="0"
          max="1"
          step="0.5"
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          aria-label="Voice stability"
          aria-valuetext={selectedOption?.label}
          className="absolute inset-0 z-10 h-4 w-full cursor-pointer appearance-none border-0 bg-transparent outline-none focus:border-0 focus:outline-none focus:ring-0 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-orange-500 [&::-moz-range-track]:h-2 [&::-moz-range-track]:border-0 [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-orange-500"
        />
      </div>
    </div>
  );
};





const mergeUniqueVoices = (...voiceGroups) => {
  const seen = new Set();

  return voiceGroups.flat().filter((voice) => {
    const voiceId = voice?.voiceId;
    if (!voiceId || seen.has(voiceId)) {
      return false;
    }

    seen.add(voiceId);
    return true;
  });
};

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return value;
  return value._id || value.id || value.voiceId || value.variantId || "";
};

const emptyBackgroundMedia = {
  backgroundMediaUrl: null,
  type: null,
  preview: null,
  appearance: null,
  isUploading: false,
};

const normalizeBackgroundMedia = (media) => {
  if (!media) return emptyBackgroundMedia;

  const url = media.backgroundMediaUrl || media.url || media.file?.url || null;
  if (!url) return emptyBackgroundMedia;

  const rawType = (media.type || media.mimeType || "video").toString().toLowerCase();
  const type = rawType.startsWith("video") ? "video" : "image";

  return {
    backgroundMediaUrl: url,
    type,
    preview: media.preview || url,
    appearance: media.appearance || null,
    isUploading: false,
  };
};

export default function TalkingHead({ params }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    script, setScript, addGeneratedScript, currentScriptNumber, scriptCount,
    goToPreviousScript, goToNextScript, resetScriptHistory,
  } = useScriptHistory("");
  const regenerationData = location.state?.regenerationData;
  const regenerationSceneVariationId = getEntityId(
    regenerationData?.sceneVariationId || regenerationData?.sceneCharacterId || regenerationData?.characterId
  );
  const regenerationOriginalCharacterId = getEntityId(
    regenerationData?.originalCharacterId || regenerationData?.characterId
  );

  useRef(null);
  useRef(null);
  useRef(null);
  useRef(null);

  // REMOVED all theme state and effects
  useState("system");


  useState("");
  useState("Conversational");
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  useState(1);
  const [showScriptPrompt, ] = useState(false);
  const [scriptPrompt, setScriptPrompt] = useState("");
  const [voiceGenerations, ] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentGenerationIndex, setCurrentGenerationIndex] = useState(-1);
  useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);

  const instructionsTextareaRef = useRef(null);
  const scriptTextareaRef = useRef(null);
  const voiceSettingsRef = useRef(null);
  const voiceSettingsBtnRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showVoiceSettings &&
        voiceSettingsRef.current &&
        !voiceSettingsRef.current.contains(event.target) &&
        voiceSettingsBtnRef.current &&
        !voiceSettingsBtnRef.current.contains(event.target)
      ) {
        setShowVoiceSettings(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showVoiceSettings]);

  const fetchIdRef = useRef(0);


  const loadGeneration = (index) => {
    setCurrentGenerationIndex(index);
  };





  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [, setLibraryCharacter] = useState(null);
  useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState(() => {
    const regCharacter = location.state?.regenerationData?.originalCharacterId;
    return getEntityId(regCharacter || location.state?.selectedCharacterId);
  });
  const [prioritizeReturnedCharacter, setPrioritizeReturnedCharacter] = useState(
    () => location.state?.characterSelectionSource === "all-characters"
  );
  const [fixedCarouselCharacterIds, setFixedCarouselCharacterIds] = useState([]);
  const [resolvedRegenerationCharacter, setResolvedRegenerationCharacter] = useState(null);

  const allCharacters = useSelector(
    (state) => state.data.apiData.characters?.data || []
  );

  const loading = useSelector((state) => state.data.loading.characters);

  const restoredCharacterFromState =
    (location.state?.regenerationData?.originalCharacter &&
      typeof location.state.regenerationData.originalCharacter === "object" &&
      location.state.regenerationData.originalCharacter) ||
    (location.state?.selectedCharacterId &&
      typeof location.state.selectedCharacterId === "object" &&
      location.state.selectedCharacterId) ||
    null;

  const voicesDataFromRedux = useSelector(
    (state) => state.data.apiData.studioVoices?.data?.suggestedVoices || []
  );
  useSelector(
    (state) => state.data.apiData.getCharacterById?.data || []
  );

  useSelector(
    (state) => state.data.loading.getCharacterById
  );

  const loadingVoices = useSelector((state) => state.data.loading.studioVoices);

  // Local state to accumulate voices for infinite scroll
  const [accumulatedVoices, setAccumulatedVoices] = useState([]);
  const [isSyncingVoices, setIsSyncingVoices] = useState(false);

  const loadingVideo = useSelector((state) => state.data.loading.generateVideo);

  const getBrandName = (brand) => {
    if (!brand || typeof brand === "string") return "";
    return brand.brandName || brand.name || "";
  };

  const getCharacters = async () => {
    await services.getCharacters();
  };
  const getVoices = async (gender = "", selectedLanguage = "", age = "", accent = "", category = "", search = "", page = 1, pageSize = 50) => {
    const payload = { page, pageSize };
    if (gender && gender !== "all") payload.gender = gender;
    if (selectedLanguage) {
      payload.language = selectedLanguage.startsWith("en") ? "en" : selectedLanguage;
      if (selectedLanguage.startsWith("en")) {
        payload.locale = selectedLanguage === "en-uk" ? "en-GB" : "en-US";
      }
    }
    if (age && age !== "all") {
      payload.age = age;
    }

    if (accent && accent !== "all") payload.accent = accent;
    if (category && category !== "all") payload.category = category;
    if (search) payload.search = search;

    await services.studioVoices(payload);
  };

  // Load characters on mount
  useEffect(() => {
    getCharacters();
    getVoices();
  }, []);

  // Scroll to top on mount / navigation
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.state]);

  // Handle brand selection from navigation state
  useEffect(() => {
    if (location.state?.brandId) {
      const bId = getEntityId(location.state.brandId);
      setSelectedBrandId(bId);
      localStorage.setItem("selectedBrandId", bId);
    }
  }, [location.state]);

  // Handle pre-selected character and restored state from library
  // NOTE: When coming from VideoEditor via "Generate New Scene", all data lives in
  // location.state.regenerationData which is handled by the dedicated regeneration
  // effect below. We must NOT overwrite those values with flat props here.
  useEffect(() => {
    if (!location.state) return;
    const state = location.state;
    const isRegenerationNav = Boolean(state.regenerationData);

    if (isRegenerationNav) {
      setPrioritizeReturnedCharacter(false);
      // Regeneration flow handled entirely by the regenerationData effect below.
      // Nothing to do here \u2014 avoid overwriting with empty flat props.
      return;
    }

    // Normal navigation flow (e.g. returning from character library)
    if (allCharacters.length > 0 && state.selectedCharacterId !== undefined) {
      const returnedCharacterId = getEntityId(state.selectedCharacterId);
      setSelectedCharacter(returnedCharacterId);
      setPrioritizeReturnedCharacter(state.characterSelectionSource === "all-characters");
    }
    if (state.script !== undefined) setScript(state.script);
    if (state.selectedVoice !== undefined) setSelectedVoice(state.selectedVoice);
    if (state.selectedLanguage !== undefined) setSelectedLanguage(state.selectedLanguage);
    if (state.stability !== undefined) setStability([normalizeVoiceStability(state.stability)]);
    if (state.speed !== undefined) setSpeed(state.speed);
    if (state.customPrompt !== undefined) setCustomPrompt(state.customPrompt);
    if (state.backgroundMedia !== undefined) {
      setBackgroundMedia(normalizeBackgroundMedia(state.backgroundMedia));
    }
  }, [location.state, allCharacters, setScript]);

  // Sync filters once characters load
  useEffect(() => {
    if (allCharacters.length > 0 && selectedCharacter) {
      const character = allCharacters.find(c => c._id === selectedCharacter);
      if (character) {
        const characterGender = getCharacterGender(character);
        const characterLanguage = getCharacterLanguage(character);
        if (characterGender) setVoiceFilterGender(current => current === "all" ? characterGender : current);
        if (characterLanguage) setSelectedLanguage(current => current === "en" ? characterLanguage : current);
      }
    }
  }, [allCharacters, selectedCharacter]);

  // Handle regeneration data
  useEffect(() => {
    if (location.state?.regenerationData) {
      const data = location.state.regenerationData;

      const regeneratedCharacterId = getEntityId(data.originalCharacterId);
      const regeneratedVoiceId = getEntityId(data.voiceId || data.voice);

      // Set Basic Fields
      if (data.scriptText) setScript(data.scriptText);
      if (regeneratedVoiceId) setSelectedVoice(regeneratedVoiceId);

      // Set Character
      if (regeneratedCharacterId) {
        setSelectedCharacter(regeneratedCharacterId);
      }

      // Set Brand
      const brandId = getEntityId(data.brandId);
      if (brandId) {
        setSelectedBrandId(brandId);
        localStorage.setItem("selectedBrandId", brandId);
        const brandName = getBrandName(data.brandId);
        if (brandName) setSelectedBrandName(brandName);
      }

      // Voice Settings
      if (data.voiceSettings) {
        if (data.voiceSettings.stability !== undefined) setStability([normalizeVoiceStability(data.voiceSettings.stability)]);
        if (data.voiceSettings.speed !== undefined) setSpeed([data.voiceSettings.speed]);
      }

      // Audio URL
      if (data.audioUrl) {
        setAudioUrl(data.audioUrl);
        if (audioRefGen.current) {
          audioRefGen.current.src = data.audioUrl;
          audioRefGen.current.load();
        }

        // Fetch the blob so we can re-upload it
        try {
          fetch(data.audioUrl)
            .then((res) => res.blob())
            .then((blob) => {
              setAudioBlob(blob);
            })
            .catch((err) => console.error("Error fetching audio blob:", err));
        } catch (error) {
          console.error("Error fetching audio blob:", error);
        }
      }

      // Background Media
      if (data.backgroundMedia) {
        setBackgroundMedia(normalizeBackgroundMedia(data.backgroundMedia));
      }
    }
  }, [location.state, setScript]);

  const [favoritedCarouselCharacters, setFavoritedCarouselCharacters] =
    useState(() => {
      if (typeof window !== "undefined") {
        const storedFavorites = localStorage.getItem("favoriteCharacters");
        const storedCustomFavorites = localStorage.getItem(
          "favoriteCustomCharacters"
        );
        const favorites = storedFavorites ? JSON.parse(storedFavorites) : [];
        const customFavorites = storedCustomFavorites
          ? JSON.parse(storedCustomFavorites)
          : [];
        return [...favorites, ...customFavorites];
      }
      return [];
    });

  useState(false);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [selectedVoiceDetails, setSelectedVoiceDetails] = useState(null);
  const [librarySelectedVoiceDetails, setLibrarySelectedVoiceDetails] = useState(null);
  const [stability, setStability] = useState([0.5]);
  const [speed, setSpeed] = useState([1]);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  useState("");
  useState(null);
  useState(false);
  useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  useState(false);
  const [showGenerationHistory, setShowGenerationHistory] = useState(false);
  const [selectedGenerationImage, setSelectedGenerationImage] = useState(null);
  const [historyImages, setHistoryImages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);


  // ✅ REMOVED sidebar states from parent
  const [showVoiceLibrary, setShowVoiceLibrary] = useState(false);

  // Voice filter states (for API calls)
  const [voiceSearchQuery, setVoiceSearchQuery] = useState("");
  const [voiceFilterGender, setVoiceFilterGender] = useState("all");
  const [voiceFilterAge, setVoiceFilterAge] = useState("all");
  const [voiceFilterCategory, setVoiceFilterCategory] = useState("all");

  // Pagination states
  const [voicePage, setVoicePage] = useState(1);
  const [hasMoreVoices, setHasMoreVoices] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [characterImage, setCharacterImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const previewPollIntervalRef = useRef(null);
  const previewRequestTokenRef = useRef(0);
  const regenerationCharacterRequestRef = useRef(0);
  const regenerationCharacterDetailsRequestRef = useRef({ characterId: "", promise: null });
  const appliedRegenerationCharacterRef = useRef("");
  const [isResolvingRegenerationCharacter, setIsResolvingRegenerationCharacter] = useState(
    Boolean(regenerationOriginalCharacterId)
  );
  const [customPrompt, setCustomPrompt] = useState(null);
  const [selectedTone, ] = useState("Conversational");

  useEffect(() => {
    if (!regenerationOriginalCharacterId) {
      setIsResolvingRegenerationCharacter(false);
      return;
    }

    const resolutionKey = `${regenerationOriginalCharacterId}:${regenerationSceneVariationId}`;
    if (appliedRegenerationCharacterRef.current === resolutionKey) return;

    setIsResolvingRegenerationCharacter(true);
    const requestToken = ++regenerationCharacterRequestRef.current;
    let cancelled = false;

    const resolveSceneCharacterVariation = async () => {
      if (regenerationCharacterDetailsRequestRef.current.characterId !== regenerationOriginalCharacterId) {
        regenerationCharacterDetailsRequestRef.current = {
          characterId: regenerationOriginalCharacterId,
          promise: services.getCharacterById(regenerationOriginalCharacterId),
        };
      }

      const response = await regenerationCharacterDetailsRequestRef.current.promise;
      const responseData = response?.data?.data || response?.data || response;
      const details = responseData?.character || responseData;
      const variants = Array.isArray(responseData?.variants)
        ? responseData.variants
        : Array.isArray(details?.variants)
          ? details.variants
          : [];
      const matchingVariation =
        variants.find(
          (variant) => String(getEntityId(variant)) === String(regenerationSceneVariationId)
        ) ||
        variants.find(
          (variant) => String(getEntityId(variant?.characterId)) === String(regenerationOriginalCharacterId)
        );

      if (cancelled || regenerationCharacterRequestRef.current !== requestToken) return;

      appliedRegenerationCharacterRef.current = resolutionKey;
      if (details && typeof details === "object") {
        setResolvedRegenerationCharacter(details);
      }
      setSelectedCharacter(regenerationOriginalCharacterId);
      setPrioritizeReturnedCharacter(true);
      setFixedCarouselCharacterIds([regenerationOriginalCharacterId]);
      setCharacterImage(
        matchingVariation?.originalUrl || matchingVariation?.imageUrl || matchingVariation?.url || null
      );
      setSelectedGenerationImage(matchingVariation || null);
      setHistoryImages(variants);
      setIsResolvingRegenerationCharacter(false);
    };

    resolveSceneCharacterVariation().catch((error) => {
      console.error("Failed to resolve scene character variation:", error);
      if (regenerationCharacterRequestRef.current === requestToken) {
        appliedRegenerationCharacterRef.current = resolutionKey;
        setSelectedCharacter(regenerationOriginalCharacterId);
        setCharacterImage(null);
        setSelectedGenerationImage(null);
        setIsResolvingRegenerationCharacter(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [regenerationOriginalCharacterId, regenerationSceneVariationId]);

  useEffect(() => {
    if (!regenerationOriginalCharacterId || allCharacters.length === 0) return;

    setFixedCarouselCharacterIds((currentIds) => {
      if (!currentIds.includes(regenerationOriginalCharacterId)) return currentIds;

      const nextIds = [
        regenerationOriginalCharacterId,
        ...allCharacters
          .map((character) => character?._id)
          .filter((id) => id && id !== regenerationOriginalCharacterId),
      ].slice(0, 5);

      return nextIds.length === currentIds.length && nextIds.every((id, index) => id === currentIds[index])
        ? currentIds
        : nextIds;
    });
  }, [allCharacters, regenerationOriginalCharacterId]);

  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(localStorage.getItem("selectedBrandId") || "");
  const [selectedBrandName, setSelectedBrandName] = useState("");
  const [, setSelectedBrand] = useState(null);
  // Asset Library State
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [assetLibraryMode, setAssetLibraryMode] = useState('background'); // 'background' or 'scene'

  // Background Media State
  const [backgroundMedia, setBackgroundMedia] = useState({
    ...normalizeBackgroundMedia(
      location.state?.regenerationData?.backgroundMedia || location.state?.backgroundMedia
    ),
  });

  // ✅ REMOVED sidebar audio states from parent

  // Add these states at the top of your component with other states
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioInProcess, setAudioInProcess] = useState(false);
  const audioRequestRef = useRef(null);

  useEffect(() => {
    setAudioInProcess(false);
    return () => {
      audioRequestRef.current?.abort();
      audioRequestRef.current = null;
    };
  }, [selectedVoice]);
  const audioRefGen = useRef(null);
  if (!audioRefGen.current) audioRefGen.current = new Audio();
  const [audioBlob, setAudioBlob] = useState(null);

  // Add these state variables to the VideoStudioPage component
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [, setIsLoadingVoice] = useState(false);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    return () => {
      previewRequestTokenRef.current += 1;
      if (previewPollIntervalRef.current) {
        clearInterval(previewPollIntervalRef.current);
        previewPollIntervalRef.current = null;
      }
    };
  }, []);

  // Restore generated audio when returning from the character library. A new
  // object URL is required because the old component revokes its URL on exit.
  useEffect(() => {
    const state = location.state;
    if (!state || state.regenerationData || state.selectedCharacterId === undefined) return;

    const returnedCharacterId = getEntityId(state.selectedCharacterId);
    const returnedCharacter = allCharacters.find((character) => character?._id === returnedCharacterId);
    const returnedCharacterGender = getCharacterGender(returnedCharacter);
    const hasGenderChanged = state.sourceCharacterGender && returnedCharacterGender
      ? state.sourceCharacterGender !== returnedCharacterGender
      : false;

    // Voices and generated audio are gender-specific. Wait for the selected
    // character to load before restoring audio when gender metadata is present.
    if (state.sourceCharacterGender && !returnedCharacterGender) return;
    if (hasGenderChanged) {
      audioRefGen.current.pause();
      audioRefGen.current.removeAttribute("src");
      audioRefGen.current.load();
      setSelectedVoice(null);
      setAudioBlob(null);
      setAudioUrl(null);
      return;
    }

    if (state.audioBlob) {
      const restoredUrl = URL.createObjectURL(state.audioBlob);
      setAudioBlob(state.audioBlob);
      setAudioUrl(restoredUrl);
      audioRefGen.current.src = restoredUrl;
      audioRefGen.current.load();
      return;
    }

    if (state.audioUrl) {
      setAudioUrl(state.audioUrl);
      audioRefGen.current.src = state.audioUrl;
      audioRefGen.current.load();
      fetch(state.audioUrl)
        .then((response) => response.blob())
        .then((blob) => setAudioBlob(blob))
        .catch((error) => console.error("Error restoring generated audio:", error));
    }
  }, [location.state, allCharacters]);

  // Add audio event handlers for the parent component's audio
  useEffect(() => {
    const audio = audioRef.current;

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoadingVoice(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setIsLoadingVoice(false);
      setPreviewingVoiceId(null);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentlyPlayingId(null);
      setPreviewingVoiceId(null);
      setIsLoadingVoice(false);
    };

    const handleWaiting = () => setIsLoadingVoice(true);
    const handleCanPlay = () => setIsLoadingVoice(false);

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.pause();
    };
  }, []);

  // Add the handlePlayVoice function back
  const handlePlayVoice = (voiceId, previewUrl, e) => {
    audioRefGen.current.pause();
    if (e) e.stopPropagation();
    const audio = audioRef.current;

    if (currentlyPlayingId === voiceId && isPlaying) {
      // Pause if currently playing this voice
      audio.pause();
      setCurrentlyPlayingId(null);
      setPreviewingVoiceId(null);
      setIsLoadingVoice(false);
    } else {
      // Stop current audio
      if (currentlyPlayingId) {
        audio.pause();
      }

      // Set loading state
      setIsLoadingVoice(true);
      setCurrentlyPlayingId(voiceId);
      setPreviewingVoiceId(voiceId);

      // Set new audio source
      audio.src = previewUrl;
      audio.load();

      // Play the audio
      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        setCurrentlyPlayingId(null);
        setPreviewingVoiceId(null);
        setIsLoadingVoice(false);
      });
    }
  };

  // The preview Audio instance is created once and never replaced.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio) audio.pause();
    };
  }, []);





  const handleRemoveImage = (imageId) => {
    setSelectedImages((prev) => prev.filter((img) => img.id !== imageId));
  };



  const getLanguageName = (code) => {
    const languages = {
      en: "English (US)",
      "en-uk": "English (UK)",
      es: "Spanish",
      fr: "French",
      de: "German",
      it: "Italian",
      pt: "Portuguese",
      pl: "Polish",
      nl: "Dutch",
      sl: "Slovenian",
      el: "Greek",
      cs: "Czech",
      sv: "Swedish",
      da: "Danish",
      no: "Norwegian",
      fi: "Finnish",
      hu: "Hungarian",
      ro: "Romanian",
      tr: "Turkish",
    };
    return languages[code] || "Select Language";
  };



  const getCharacterGender = (character) => {
    if (!character) return "";
    return (character.attributes?.gender || character.gender || character.metadata?.gender || "")
      .toString()
      .toLowerCase();
  };

  const getCharacterLanguage = (character) => {
    if (!character) return "";
    const lang = (character.attributes?.language || character.language || character.metadata?.language || "")
      .toString()
      .toLowerCase();
    return lang === "en-us" ? "en" : lang;
  };

  const getCharacterAge = (character) => {
    if (!character) return "";
    return character.attributes?.age || character.age || character.metadata?.age || "";
  };

  const getCurrentCharacter = () => {
    return (
      allCharacters.find((c) => c?._id === selectedCharacter) ||
      (resolvedRegenerationCharacter?._id === selectedCharacter
        ? resolvedRegenerationCharacter
        : null) ||
      (restoredCharacterFromState?._id === selectedCharacter
        ? restoredCharacterFromState
        : null)
    );
  };

  const currentChar = getCurrentCharacter();
  const currentCharacterGender = getCharacterGender(currentChar);
  getCharacterAge(currentChar);
  const carouselCharacterIds = location.state?.carouselCharacterIds;

  const visibleCharacters = useMemo(() => {
    if ((!allCharacters || allCharacters.length === 0) && !resolvedRegenerationCharacter) return [];

    const findCharacter = (id) =>
      allCharacters.find((item) => item?._id === id) ||
      (resolvedRegenerationCharacter?._id === id ? resolvedRegenerationCharacter : null);

    if (fixedCarouselCharacterIds.length > 0) {
      const fixedCharacters = fixedCarouselCharacterIds
        .map(findCharacter)
        .filter(Boolean);

      if (fixedCharacters.length > 0) return fixedCharacters;
    }

    if (!selectedCharacter || !prioritizeReturnedCharacter) {
      return allCharacters.slice(0, 5);
    }

    const selectedCharacterData =
      findCharacter(selectedCharacter) ||
      (restoredCharacterFromState?._id === selectedCharacter
        ? restoredCharacterFromState
        : null);

    if (!selectedCharacterData) {
      return allCharacters.slice(0, 5);
    }

    const previousCarouselIds = carouselCharacterIds || [];
    const orderedCharacters = previousCarouselIds.length
      ? [
        ...previousCarouselIds
          .map((id) => allCharacters.find((item) => item?._id === id))
          .filter(Boolean),
        ...allCharacters.filter((item) => !previousCarouselIds.includes(item?._id)),
      ]
      : allCharacters;
    const otherCharacters = orderedCharacters.filter((item) => item?._id !== selectedCharacter);
    return [selectedCharacterData, ...otherCharacters.slice(0, 4)];
  }, [allCharacters, carouselCharacterIds, fixedCarouselCharacterIds, prioritizeReturnedCharacter, resolvedRegenerationCharacter, restoredCharacterFromState, selectedCharacter]);

  // ✅ Debounce search query to avoid excessive API calls
  // Reset to page 1 when filters change
  useEffect(() => {
    const triggerFetch = () => {
      const genderFilter = voiceFilterGender === "all" ? "" : voiceFilterGender;
      const ageFilter = voiceFilterAge === "all" ? "" : voiceFilterAge;
      const categoryFilter = voiceFilterCategory === "all" ? "" : voiceFilterCategory;
      const effectiveGender = genderFilter || currentCharacterGender || "";
      const effectiveAge = ageFilter;

      // Instant feedback: clear voices and show loading right before fetch
      setAccumulatedVoices([]);
      setVoicePage(1);
      setHasMoreVoices(true);
      setIsSyncingVoices(true);

      const currentFetchId = ++fetchIdRef.current;

      getVoices(
        effectiveGender,
        selectedLanguage,
        effectiveAge,
        "", // accent empty
        categoryFilter,
        voiceSearchQuery,
        1,
        50
      ).finally(() => {
        if (currentFetchId === fetchIdRef.current) {
          setIsSyncingVoices(false);
        }
      });
    };

    const delay = voiceSearchQuery ? 400 : 0;
    const delayDebounce = setTimeout(triggerFetch, delay);

    return () => clearTimeout(delayDebounce);
  }, [currentCharacterGender, selectedLanguage, voiceFilterGender, voiceFilterAge, voiceFilterCategory, voiceSearchQuery]);

  // Append new voices from Redux to accumulated voices
  useEffect(() => {
    if (loadingVoices) return;
    if (voicesDataFromRedux) {
      if (voicePage === 1) {
        // First page - replace
        setAccumulatedVoices(voicesDataFromRedux);
      } else if (voicesDataFromRedux.length > 0) {
        // Subsequent pages - append
        setAccumulatedVoices((prev) => {
          // Avoid duplicates using voiceId
          const existingIds = new Set(prev.map((v) => v.voiceId));
          const newVoices = voicesDataFromRedux.filter(
            (v) => !existingIds.has(v.voiceId)
          );
          return [...prev, ...newVoices];
        });
      }

      // Check if we have more voices to load
      if (voicesDataFromRedux.length < 50) {
        setHasMoreVoices(false);
      }
      setIsLoadingMore(false);
    }
  }, [voicesDataFromRedux, voicePage, loadingVoices]);

  // Load more voices function
  const loadMoreVoices = () => {
    if (isLoadingMore || !hasMoreVoices || loadingVoices) return;

    setIsLoadingMore(true);
    const nextPage = voicePage + 1;
    setVoicePage(nextPage);

    // Prepare filter values for API call
    const genderFilter = voiceFilterGender === "all" ? "" : voiceFilterGender;
    const ageFilter = voiceFilterAge === "all" ? "" : voiceFilterAge;
    const categoryFilter = voiceFilterCategory === "all" ? "" : voiceFilterCategory;

    getVoices(
      genderFilter || currentChar?.attributes?.gender || "",
      selectedLanguage,
      ageFilter || currentChar?.attributes?.age || "",
      "", // accent empty
      categoryFilter,
      voiceSearchQuery,
      nextPage,
      50 // Page size (voices per page)
    );
  };

  useEffect(() => {
    const selected = accumulatedVoices.find(
      (voice) => (voice.voiceId || voice.id) === selectedVoice
    );
    if (selected) setSelectedVoiceDetails(selected);
  }, [accumulatedVoices, selectedVoice]);

  const mergedVoices = useMemo(() => {
    const base = mergeUniqueVoices(accumulatedVoices);

    if (voiceSearchQuery || !librarySelectedVoiceDetails) return base;

    return librarySelectedVoiceDetails
      ? mergeUniqueVoices([{ ...librarySelectedVoiceDetails, voiceId: librarySelectedVoiceDetails.voiceId || librarySelectedVoiceDetails.id }], base)
      : base;
  }, [accumulatedVoices, librarySelectedVoiceDetails, voiceSearchQuery]);

  const visibleVoices = useMemo(() => {
    return mergeUniqueVoices(
      librarySelectedVoiceDetails
        ? [{ ...librarySelectedVoiceDetails, voiceId: librarySelectedVoiceDetails.voiceId || librarySelectedVoiceDetails.id }]
        : [],
      mergedVoices
    ).slice(0, 5);
  }, [mergedVoices, librarySelectedVoiceDetails]);

  useMemo(
    () =>
      mergedVoices.find((voice) => (voice.voiceId || voice.id) === selectedVoice) ||
      (selectedVoiceDetails &&
      (selectedVoiceDetails.voiceId || selectedVoiceDetails.id) === selectedVoice
        ? selectedVoiceDetails
        : null) ||
      defaultVoices.find((voice) => voice.id === selectedVoice) ||
      null,
    [mergedVoices, selectedVoice, selectedVoiceDetails]
  );

  // Auto-expand textarea whenever script changes
  useEffect(() => {
    if (scriptTextareaRef.current) {
      scriptTextareaRef.current.style.height = "auto";
      scriptTextareaRef.current.style.height = scriptTextareaRef.current.scrollHeight + "px";
    }
  }, [script]);

  const insertVoiceCue = (cue) => {
    const before = script.substring(0, cursorPosition);
    const after = script.substring(cursorPosition);
    const newScript = before + ` ${cue} ` + after;
    setScript(newScript);
    const newPosition = cursorPosition + cue.length + 2;
    setCursorPosition(newPosition);

    setTimeout(() => {
      const textarea = document.getElementById("script-textarea");
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };





  const handleSelectFromAssetLibrary = (assets) => {
    if (assets && assets.length > 0) {
      if (assetLibraryMode === 'background') {
        const asset = assets[0];
        const isVideo = asset.type === 'video' || asset.mimeType?.startsWith('video/');
        const type = isVideo ? 'video' : 'image';

        setBackgroundMedia({
          backgroundMediaUrl: asset.url,
          type: type,
          preview: asset.url,
          isUploading: false
        });
      } else {
        // Scene mode - add to selectedImages
        const newImages = assets.map(asset => ({
          id: asset._id || asset.id || Math.random().toString(36),
          url: asset.url,
          preview: asset.url,
          isFromLibrary: true
        }));
        setSelectedImages(prev => [...prev, ...newImages]);
      }
      setShowAssetLibrary(false);
    }
  };

  const handleCharacterSelect = (character) => {
    if (!character) return;
    regenerationCharacterRequestRef.current += 1;
    setIsResolvingRegenerationCharacter(false);
    previewRequestTokenRef.current += 1;
    if (previewPollIntervalRef.current) {
      clearInterval(previewPollIntervalRef.current);
      previewPollIntervalRef.current = null;
    }
    setIsProcessing(false);
    setPrioritizeReturnedCharacter(false);
    setSelectedCharacter(character?._id);
    setLibraryCharacter(character.isLibrary ? character : null);
    setCharacterImage(null);
    setSelectedGenerationImage(null);

    // Sync filters with character attributes
    const characterGender = getCharacterGender(character);
    const characterLanguage = getCharacterLanguage(character);
    const shouldRefreshVoices = Boolean(
      characterGender && currentCharacterGender && characterGender !== currentCharacterGender
    );

    // Voices and generated audio are only cleared for a male/female switch.
    if (shouldRefreshVoices) {
      setSelectedVoice(null);
      setAudioUrl(null);
      setAudioBlob(null);
    }

    if (characterGender && shouldRefreshVoices) {
      setVoiceFilterGender(characterGender);
    }
    if (characterLanguage && shouldRefreshVoices) {
      setSelectedLanguage(characterLanguage);
    }

    // Keep the current voice list when switching between characters of the same gender.
    if (shouldRefreshVoices) {
      setVoicePage(1);
      setAccumulatedVoices([]);
      setHasMoreVoices(true);
      setIsSyncingVoices(true);
    }
  };



  const toggleCarouselFavorite = (characterId, e) => {
    e.stopPropagation();



    setFavoritedCarouselCharacters((prev) => {
      const newFavorites = prev.includes(characterId)
        ? prev.filter((id) => id !== characterId)
        : [...prev, characterId];

      if (typeof window !== "undefined") {
        const numericFavorites = newFavorites.filter(
          (id) => typeof id === "number"
        );
        const stringFavorites = newFavorites.filter(
          (id) => typeof id === "string"
        );
        localStorage.setItem(
          "favoriteCharacters",
          JSON.stringify(numericFavorites)
        );
        localStorage.setItem(
          "favoriteCustomCharacters",
          JSON.stringify(stringFavorites)
        );
      }

      return newFavorites;
    });
  };


  const createPreview = async () => {
    previewRequestTokenRef.current += 1;
    const requestToken = previewRequestTokenRef.current;
    if (previewPollIntervalRef.current) {
      clearInterval(previewPollIntervalRef.current);
      previewPollIntervalRef.current = null;
    }

    const sourceVariant = selectedGenerationImage;
    let editedCharacterImg = sourceVariant?.originalUrl || characterImage || currentChar?.baseImage?.originalUrl || null;

    setIsProcessing(true);
    let uploadedImageUrls = [];

    if (editedCharacterImg) {
      uploadedImageUrls.push(editedCharacterImg);
    }

    if (selectedImages.length > 0) {
      try {
        // Upload images sequentially to avoid overwhelming the server
        for (const image of selectedImages) {
          if (image.isFromLibrary || image.url) {
            uploadedImageUrls.push(image.url);
          } else {
            const formData = new FormData();
            formData.append("file", image?.file);

            const uploadResponse = await services.uploadImage(formData); // Assuming you have an upload service
            if (uploadResponse.statusCode === 201) {
              uploadedImageUrls.push(uploadResponse.data.url); // Adjust based on your API response structure
            }
          }
        }
      } catch (error) {
        console.error("Error uploading images:", error);
        setIsProcessing(false);
        return;
      }
    }

    let res = null;
    let body = {};

    body = {
      editPrompt: customPrompt,
      referenceImageUrls: uploadedImageUrls,
    };
    if (editedCharacterImg) {
      body.imageUrl = editedCharacterImg;
    }
    const sourceVariantId = getEntityId(sourceVariant);
    if (sourceVariantId) {
      body.variantId = sourceVariantId;
    }

    try {
      res = await services.editCharacter(selectedCharacter, body);
      if (previewRequestTokenRef.current !== requestToken) return;
      const newVariantId = getEntityId(res?.data?.variantId || res?.data?._id);

      let checkCount = 0;
      const maxChecks = 100;

      const checkStatus = async () => {
        try {
          checkCount++;
          const resImage = await services.getCharacterById(selectedCharacter);
          if (previewRequestTokenRef.current !== requestToken) return;
          const variants = resImage?.data?.variants;
          const currentStatus = resImage?.data?.status;

          let isVariantFinished = false;

          if (newVariantId && variants) {
            const targetVariant = variants.find(
              (variant) =>
                String(getEntityId(variant)) === String(newVariantId) ||
                String(getEntityId(variant?.variantId)) === String(newVariantId)
            );

            if (targetVariant) {
              if (targetVariant.status === "active" && targetVariant.originalUrl) {
                setCharacterImage(targetVariant.originalUrl);
                setSelectedGenerationImage(targetVariant);
                isVariantFinished = true;
              } else if (targetVariant.status !== "editing" && targetVariant.status !== "processing") {
                isVariantFinished = Boolean(targetVariant.originalUrl);
              }
            }
          }

          // Fallback
          if (!isVariantFinished && (!newVariantId || !variants)) {
            if (currentStatus !== "editing" && currentStatus !== "processing") {
              if (variants && variants.length > 0) {
                const fallbackVariant = variants[variants.length - 1];
                setCharacterImage(fallbackVariant?.originalUrl);
                setSelectedGenerationImage(fallbackVariant);
              }
              isVariantFinished = true;
            }
          }

          if (isVariantFinished || checkCount >= maxChecks) {
            if (previewPollIntervalRef.current) {
              clearInterval(previewPollIntervalRef.current);
              previewPollIntervalRef.current = null;
            }
            setIsProcessing(false);
            if (checkCount >= maxChecks) {
              toastify("error", "Character generation timed out. Please check again later.");
            }
          }
        } catch (err) {
          if (previewRequestTokenRef.current !== requestToken) return;
          console.error("Error checking character status:", err);
          if (previewPollIntervalRef.current) {
            clearInterval(previewPollIntervalRef.current);
            previewPollIntervalRef.current = null;
          }
          setIsProcessing(false);
        }
      };

      if (selectedCharacter) {
        previewPollIntervalRef.current = setInterval(checkStatus, 5000);
        checkStatus();
      } else {
        setIsProcessing(false);
      }
    } catch (err) {
      if (previewRequestTokenRef.current !== requestToken) return;
      console.error("Error editing character:", err);
      setIsProcessing(false);
    }
  };

  // Add cleanup effect
  useEffect(() => {
    const audio = audioRefGen.current;

    return () => {
      // Clean up audio URL when component unmounts
      if (audioUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(audioUrl);
      }
      audio.pause();
    };
  }, [audioUrl]);

  const generateAudio = async () => {
    if (audioRequestRef.current || audioInProcess) return;
    if (!script.trim()) {
      toastify("error", "Enter a script before generating audio.");
      return;
    }

    if (!selectedVoice) {
      toastify("error", "Choose a voice before generating audio.");
      return;
    }

    const controller = new AbortController();
    audioRequestRef.current = controller;
    setAudioInProcess(true);

    audioRef.current.pause();

    // Stop currently playing audio
    if (audioRefGen.current) {
      audioRefGen.current.pause();
    }

    try {
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${process.env.REACT_APP_API}voices/generate-audio`,
        {
          voice_id: selectedVoice,
          text: script,
          voice_settings: {
            stability: stability[0],
            similarity_boost: 0.75,
            style: 0.5,
            speed: speed[0],
            use_speaker_boost: false,
          },
        },
        { responseType: "blob", signal: controller.signal, headers: { Authorization: `Bearer ${token}` } }
      );

      if (controller.signal.aborted) return;

      // Always a blob because responseType=blob
      const audioBlob = res.data;
      if (!audioBlob?.size) throw new Error("No audio was returned. Please try again.");

      // Save blob for upload later
      setAudioBlob(audioBlob);

      // Create URL for playback
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);

      // Set audio element source
      audioRefGen.current.src = url;

      audioRefGen.current.load();
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("Error generating audio:", err);
      let errorMsg = "Failed to Generate speech.";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) errorMsg = parsed.message;
        } catch (_) { }
      } else if (err?.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err?.message) {
        errorMsg = err.message;
      }
      if (errorMsg === "text is required") {
        errorMsg = "Enter a script before generating audio.";
      } else if (errorMsg === "voice_id is required") {
        errorMsg = "Choose a voice before generating audio.";
      }
      toastify("error", errorMsg);
    } finally {
      if (audioRequestRef.current === controller) {
        audioRequestRef.current = null;
        setAudioInProcess(false);
      }
    }
  };

  const handleGenerateScript = async () => {
    if (isGeneratingScript) return;
    const brandId = selectedBrandId;

    if (!brandId) {
      console.error("No brand selected");
      return;
    }

    setIsGeneratingScript(true);

    try {
      const response = await services.generateScript({ brandId, voice_settings: { tone: selectedTone } });

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        addGeneratedScript(response?.data?.script || response?.data || "");
      }
    } catch (error) {
      console.error("Error generating script:", error);
    } finally {
      setIsGeneratingScript(false);
    }
  };

  const generateVideoAd = async () => {
    const brandId = selectedBrandId;
    const char = getCurrentCharacter();

    const formData = new FormData();

    formData.append("characterId", char?._id);
    formData.append("voiceId", selectedVoice);
    formData.append("scriptText", script);
    formData.append("video_title", "Created with Zimzee AI");
    formData.append("enhance_custom_motion_prompt", true);
    formData.append("custom_motion_prompt", customPrompt);
    formData.append("brandId", brandId);

    // Use the exact variation shown in the right-side preview.
    if (characterImage) {
      formData.append("imageUrl", characterImage);
    }
    const selectedVariantId = getEntityId(selectedGenerationImage);
    if (selectedVariantId) {
      formData.append("variantId", selectedVariantId);
    }

    // IMPORTANT: append AUDIO FILE (Blob) or URL
    if (audioBlob) {
      formData.append("audioFile", audioBlob, "audio.mp3");
    } else if (audioUrl && audioUrl.startsWith('http')) {
      // If we have a remote URL but no blob, fetch it now
      try {
        const res = await axios.get(audioUrl, { responseType: 'blob' });
        if (res.data) {
          formData.append("audioFile", res.data, "audio.mp3");
        } else {
          throw new Error("Empty response from audio URL");
        }
      } catch (error) {
        console.error("Failed to fetch audio blob for upload:", error);
        alert("Could not retrieve the pre-generated audio file. Please click 'Generate speech' to create a new one.");
        return; // STOP execution
      }
    }

    formData.append("ad_type", "talking_head");
    formData.append("adType", "talking_head");
    formData.append("gender", char?.attributes?.gender || char?.gender || "");
    if (selectedLanguage) {
      formData.append("language", selectedLanguage.startsWith("en") ? "en" : selectedLanguage);
      if (selectedLanguage.startsWith("en")) {
        formData.append("locale", selectedLanguage === "en-uk" ? "en-GB" : "en-US");
      }
    }
    formData.append("voiceSettings", JSON.stringify({
      stability: stability[0],
      speed: speed[0]
    }));

    if (backgroundMedia.backgroundMediaUrl) {
      formData.append("backgroundMedia", JSON.stringify({
        backgroundMediaUrl: backgroundMedia.backgroundMediaUrl,
        type: backgroundMedia.type
      }));
    }

    await services.generateVideo(formData);

    // Clear audio URL
    setAudioUrl(null);

    // Clear audio blob
    setAudioBlob(null);

    // Keep the player reusable after a video is submitted.
    audioRefGen.current.pause();
    audioRefGen.current.removeAttribute("src");
    audioRefGen.current.load();

    setAudioInProcess(false);

    // Clear script state
    resetScriptHistory();

    // Clear voice state
    setSelectedVoice(null);

    // Clear prompt state
    setCustomPrompt("");

    // Clear loading state
    // setIsLoading(false);

    // Clear loading state
    // setIsLoading(false);

    // Navigate with tab value
    navigate("/app/ads", { state: { tab: "video" } });
  };

  const filteredBrands = brands.filter(
    (brand) =>
      brand?.internalName
        ?.toLowerCase()
        .includes(brandSearchTerm?.toLowerCase()) ||
      brand?.description
        ?.toLowerCase()
        ?.includes(brandSearchTerm?.toLowerCase()) ||
      brand?.industry?.toLowerCase()?.includes(brandSearchTerm?.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBrandDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const urlSearchParams = new URLSearchParams(window.location.search);
    const brandId = urlSearchParams.get("brandId");
    const createdBrandName = urlSearchParams.get("brandName");

    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API}brands`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setBrands(response.data.data);

        let brand;
        const targetId = brandId || localStorage.getItem("selectedBrandId");

        if (targetId) {
          brand = response.data.data.find((b) => b._id === targetId);
          if (brand) {
            setSelectedBrandId(brand._id);
          }
        }

        // fallback to 0 index if no match found
        if (!brand && response.data.data.length > 0) {
          brand = response.data.data[0];
          localStorage.setItem("selectedBrandId", response.data.data[0]._id);
          setSelectedBrandId(response.data.data[0]._id);
        }

        if (!brand) return;
        let brandDetail = {
          _id: brand._id || "",
          name: brand.brandName || "",
          internalName: brand?.internalName || "",
          description: brand.description || "",
          industry: brand.industry || "",
          colors: brand.brandColors || [],
          tone: brand.brandToneAndVoice || "",
          website: brand.url || "",
          targetMarket: brand.targetMarket || "",
          uniqueValue: brand?.uniqueValue || "",
          mainJob: brand.mainJobOfProduct || "",
          differentiation: brand.differentiation || "",
          howItHelps: brand.howItHelps || "",
          features: brand?.features || "",
          benefits: brand.benefits || "",
        };

        if (brandId) {
          setSelectedBrandId(brandId);
          setSelectedBrandName(createdBrandName);
          return;
        }

        setSelectedBrand(brandDetail);
        setSelectedBrandName(brand?.internalName || brand?.brandName || "");
      } catch (err) {
        console.error(err, "err");
      }
    };
    fetchBrands();
  }, []);

  const handleBrandSelect = async (brand) => {
    localStorage.setItem("selectedBrandId", brand);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}brands/${brand}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // setIsLoading(false);
      setSelectedBrandName(response.data.data.internalName || response.data.data.brandName);
      setSelectedBrandId(response.data.data._id);
      const brandDetail = {
        _id: response.data.data._id,
        name: response.data.data.brandName,
        internalName: response.data.data.internalName || response.data.data.brandName,
        description: response.data.data.description,
        industry: response.data.data.industry,
        colors: response.data.data.brandColors,
        tone: response.data.data.brandToneAndVoice,
        website: response.data.data.url,
        targetMarket: response.data.data.targetMarket,
        uniqueValue:
          "AI-powered automation that learns from your team's workflow",
        mainJob: response.data.data.mainJobOfProduct,
        differentiation: response.data.data.differentiation,
        howItHelps: response.data.data.howItHelps,
        features: response.data.data.features,
        benefits: response.data.data.benefits,
      };
      setSelectedBrand(brandDetail);
      setShowBrandDropdown(false);
      setBrandSearchTerm("");
    } catch (err) {
      setShowBrandDropdown(false);
      setBrandSearchTerm("");
    } finally {
      setShowBrandDropdown(false);
      setBrandSearchTerm("");
    }
  };

  // Handle voice selection and clear audio
  const handleVoiceSelection = (voiceId, voice, options = {}) => {
    setSelectedVoice(voiceId);
    if (voice) setSelectedVoiceDetails(voice);
    if (voice && options.fromVoiceLibrary) {
      setLibrarySelectedVoiceDetails(voice);
    }
    // Clear audio when voice changes
    setAudioUrl(null);
    setAudioBlob(null);
    if (audioRefGen.current) {
      audioRefGen.current.pause();
      audioRefGen.current.removeAttribute("src");
      audioRefGen.current.load();
    }
  };

  // ✅ Step 1 — Wrap sidebar with useMemo
  const MemoSidebar = <VoiceLibrarySidebar
        showVoiceLibrary={showVoiceLibrary}
        setShowVoiceLibrary={setShowVoiceLibrary}
        selectedVoice={selectedVoice}
        setSelectedVoice={handleVoiceSelection}
        voicesData={mergedVoices}
        voiceSearchQuery={voiceSearchQuery}
        setVoiceSearchQuery={setVoiceSearchQuery}
        voiceFilterGender={voiceFilterGender}
        setVoiceFilterGender={setVoiceFilterGender}
        voiceFilterAge={voiceFilterAge}
        setVoiceFilterAge={setVoiceFilterAge}
        voiceFilterCategory={voiceFilterCategory}
        setVoiceFilterCategory={setVoiceFilterCategory}
        loadMoreVoices={loadMoreVoices}
        hasMoreVoices={hasMoreVoices}
        isLoadingMore={isLoadingMore}
        loadingVoices={loadingVoices || isSyncingVoices}
        voicePage={voicePage}
        prioritizedVoiceId={librarySelectedVoiceDetails?.voiceId || librarySelectedVoiceDetails?.id}
      />;





  const setHistoryCharacter = (character) => {
    setSelectedGenerationImage(character);
  };

  const handleShowHistory = async () => {
    setShowGenerationHistory(true);
    if (selectedCharacter) {
      setIsLoadingHistory(true);
      try {
        const res = await services.getCharacterById(selectedCharacter);
        if (res?.data?.variants) {
          setHistoryImages(res.data.variants);
        }
      } catch (error) {
        console.error("Error fetching history:", error);
      } finally {
        setIsLoadingHistory(false);
      }
    }
  };

  return (
    <div className="responsive-page responsive-page--talking-head min-h-screen bg-[#FAFAFA] dark:bg-gray-950 flex flex-col">
      {loading || isResolvingRegenerationCharacter ? (
        <Loading />
      ) : (
        <>
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header */}
            <header className="h-16 flex items-center justify-between px-8 shrink-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
              <Link
                to="/app/ads"
                className="flex items-center gap-2 text-sm font-medium transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to AI Studio
              </Link>
              {/* 
              <div className="flex items-center gap-4">
                <button
                  onClick={cycleTheme}
                  className="p-2 rounded-xl transition-all duration-200 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
                  title={theme === "system" ? "System theme" : theme === "light" ? "Light theme" : "Dark theme"}
                >
                  {theme === "light" ? (
                    <Sun className="h-5 w-5" />
                  ) : theme === "dark" ? (
                    <Moon className="h-5 w-5" />
                  ) : (
                    <Monitor className="h-5 w-5" />
                  )}
                </button>
                <button className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800">
                  <Bell className="h-5 w-5" />
                </button>
                <div className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-orange-100 dark:ring-orange-900/50">
                  <img src="/professional-user-profile-photo.jpg" alt="User" className="w-full h-full object-cover" />
                </div>
              </div> */}
            </header>

            {/* Main Content */}
            <div className="talking-head-layout flex-1 flex">
              {/* Left Panel - All Sections */}
              <div className="talking-head-editor flex-1">
                <div className="max-w-5xl mx-auto px-4 py-6 lg:px-6">
                  {/* Page Title */}
                  <div className="flex items-center justify-between mb-8">
                    <div className="space-y-1">
                      {selectedBrandName && (
                        <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                          {selectedBrandName}
                        </p>
                      )}
                      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Create Talking Head
                      </h1>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Customize your character, script, and voice to generate a video
                      </p>
                    </div>

                    {/* Brand Selection Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                      <div className="relative">
                        <button
                          className="gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium inline-flex items-center justify-between w-64 transition-all shadow-sm"
                          onClick={() => setShowBrandDropdown(!showBrandDropdown)}
                        >
                          <span className="truncate w-full text-left flex items-center gap-2">
                            {selectedBrandName || "Select a brand"}
                          </span>
                          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${showBrandDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showBrandDropdown && (
                          <div className="absolute top-full right-0 mt-2 w-72 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                            {/* Search input */}
                            <div className="p-3 border-b border-gray-100 dark:border-gray-800">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                  placeholder="Search brands..."
                                  value={brandSearchTerm}
                                  onChange={(e) => setBrandSearchTerm(e.target.value)}
                                  className="pl-10 h-10 border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 w-full transition-all"
                                />
                              </div>
                            </div>

                            {/* Brand list */}
                            <div className="max-h-64 overflow-y-auto p-1">
                              {filteredBrands.length > 0 ? (
                                filteredBrands.map((brand, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      handleBrandSelect(brand._id);
                                      setShowBrandDropdown(false);
                                    }}
                                    className={`w-full px-3 py-2.5 rounded-xl text-left flex items-center gap-3 text-sm transition-colors ${selectedBrandId === brand._id
                                      ? "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 font-medium"
                                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                                      }`}
                                  >
                                    <div className={`w-2 h-2 rounded-full ${selectedBrandId === brand._id ? "bg-orange-500" : "bg-transparent"}`} />
                                    <span className="truncate">{brand?.internalName || brand?.brandName}</span>
                                    {selectedBrandId === brand._id && (
                                      <Check className="h-4 w-4 ml-auto" />
                                    )}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-8 text-center">
                                  <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">No brands found</div>
                                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">Try adjusting your search</div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Character & Language */}
                  <div className="rounded-2xl p-6 shadow-sm mb-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-semibold flex items-center justify-center">1</span>
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Choose Character & Language</h2>
                      </div>
                      <Menu as="div" className="relative">
                        <Menu.Button className="flex items-center gap-2 rounded-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                          <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          {getLanguageName(selectedLanguage)}
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-50 max-h-80 overflow-y-auto">
                            <div className="p-1">
                              {Object.entries({
                                en: "🇺🇸 English (US)",
                                "en-uk": "🇬🇧 English (UK)",
                                es: "🇪🇸 Spanish",
                                "es-MX": "🇲🇽 Spanish (Mexico)",
                                fr: "🇫🇷 French",
                                de: "🇩🇪 German",
                                it: "🇮🇹 Italian",
                                pt: "🇵🇹 Portuguese",
                                pl: "🇵🇱 Polish",
                                nl: "🇳🇱 Dutch",
                                ja: "🇯🇵 Japanese",
                                ko: "🇰🇷 Korean",
                                "zh-CN": "🇨🇳 Chinese (Simplified)",
                                hi: "🇮🇳 Hindi",
                                ar: "🇸🇦 Arabic",
                                sl: "🇸🇮 Slovenian",
                                el: "🇬🇷 Greek",
                                cs: "🇨🇿 Czech",
                                sv: "🇸🇪 Swedish",
                                da: "🇩🇰 Danish",
                                no: "🇳🇴 Norwegian",
                                fi: "🇫🇮 Finnish",
                                hu: "🇭🇺 Hungarian",
                                ro: "🇷🇴 Romanian",
                                tr: "🇹🇷 Turkish"
                              }).map(([code, label]) => (
                                <Menu.Item key={code}>
                                  {({ active }) => (
                                    <button
                                      onClick={() => setSelectedLanguage(code)}
                                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-gray-100 dark:bg-gray-700" : ""
                                        } ${selectedLanguage === code ? "text-orange-500 font-medium" : "text-gray-900 dark:text-white"
                                        }`}
                                    >
                                      {label}
                                    </button>
                                  )}
                                </Menu.Item>
                              ))}
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>

                    {/* Character Grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                      {visibleCharacters.map((character, index) => {
                        const isFavorited = favoritedCarouselCharacters.includes(character?._id);
                        let hideClass = "";
                        if (index >= 4) hideClass = "hidden lg:block";
                        else if (index >= 3) hideClass = "hidden md:block";
                        else if (index >= 2) hideClass = "hidden sm:block";
                        return (
                          <div
                            key={character?._id}
                            className={`relative group ${hideClass}`}
                          >
                            <button
                              onClick={() => handleCharacterSelect(character)}
                              className={`w-full rounded-xl transition-all ${selectedCharacter === character?._id
                                ? "ring-2 ring-orange-500"
                                : "hover:scale-105"
                                }`}
                            >
                              <div className="aspect-[9/16] relative rounded-xl overflow-hidden">
                                <img
                                  src={character?.baseImage?.originalUrl || "/placeholder.svg"}
                                  alt={character.name}
                                  className="w-full h-full object-cover"
                                />
                                {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" /> */}

                                <div className="absolute bottom-0 left-0 right-0 p-3 flex flex-col">
                                  {(character.isCustom || character.visibility === "private") && (
                                    <span className="self-start px-2.5 py-0.5 bg-neutral-700 text-white text-[10px] font-semibold rounded uppercase mb-1">
                                      Custom
                                    </span>
                                  )}
                                  <p className="text-white font-semibold text-sm truncate text-center">{character.name}</p>
                                </div>
                              </div>
                            </button>

                            {/* Favorite Heart Button */}
                            <button
                              onClick={(e) => toggleCarouselFavorite(character?._id, e)}
                              className={`absolute top-2 right-2 w-7 h-7 rounded-lg flex items-center justify-center transition-all z-10 ${isFavorited
                                ? "bg-orange-500 opacity-100"
                                : "bg-black/40 opacity-0 group-hover:opacity-100"
                                }`}
                            >
                              <Heart className={`w-4 h-4 ${isFavorited ? "text-white fill-white" : "text-white"}`} />
                            </button>
                          </div>
                        )
                      })}

                      {/* View All Characters Card */}
                      <button
                        onClick={() => navigate("/app/characters", {
                          state: {
                            returnTo: location.pathname,
                            characterSelectionSource: "all-characters",
                            carouselCharacterIds: visibleCharacters.map((character) => character?._id).filter(Boolean),
                            script,
                            selectedVoice,
                            selectedLanguage,
                            stability,
                            speed,
                            customPrompt,
                            backgroundMedia,
                            brandId: selectedBrandId,
                            sourceCharacterGender: currentCharacterGender,
                            audioBlob,
                            audioUrl: audioUrl?.startsWith("http") ? audioUrl : undefined
                          }
                        })}
                        className="block"
                      >
                        <div className="aspect-[9/16] rounded-xl border-2 border-dashed border-orange-400 flex flex-col items-center justify-center gap-3 hover:border-orange-500 transition-all bg-white/50 dark:bg-black/20">
                          <span className="text-orange-500 text-sm font-medium text-center leading-tight">
                            View all<br />characters
                          </span>
                          <ChevronRight className="w-5 h-5 text-orange-500" />
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Section 2: Scene Customization */}
                  <div className="rounded-2xl p-6 shadow-sm mb-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                      <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-semibold flex items-center justify-center">2</span>
                      <h2 className="min-w-0 flex-1 text-base font-semibold text-gray-900 dark:text-white">
                        Customize {currentChar?.name || "Character"}'s scene
                      </h2>
                      <span className="shrink-0 whitespace-nowrap px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">Optional</span>
                    </div>

                    <div className="flex flex-col gap-3 lg:flex-row">
                      <div className="flex-1 min-w-0 w-full">
                        <textarea
                          value={customPrompt}
                          onChange={(e) => {
                            setCustomPrompt(e.target.value)
                            e.target.style.height = "auto"
                            e.target.style.height = Math.max(80, e.target.scrollHeight) + "px"
                          }}
                          placeholder="E.g., Make them wear a red shirt, hold a product, or stand in Times Square..."
                          className="w-full min-h-[80px] rounded-xl resize-none text-sm leading-relaxed p-3 focus:border-orange-300 focus:ring-orange-100 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-500 focus:outline-none"
                        />
                        {selectedImages.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-3">
                            {selectedImages.map((image) => (
                              <div key={image.id} className="relative group">
                                <img src={image.preview} alt="Preview" className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200 dark:border-white/10 group-hover:border-orange-300 transition-colors" />
                                <button onClick={() => handleRemoveImage(image.id)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 shadow-lg"><X className="w-3 h-3 text-white" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex w-full flex-col gap-2 lg:w-auto lg:shrink-0">
                        <button
                          onClick={createPreview}
                          disabled={isProcessing}
                          className="inline-flex w-full items-center justify-center bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full px-4 h-10 text-sm font-medium transition-colors"
                        >
                          {isProcessing ? (
                            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                          ) : (
                            <Wand2 className="w-4 h-4 mr-0.5" />
                          )}
                          Update Character Scene
                        </button>

                        <button onClick={() => { setAssetLibraryMode('scene'); setShowAssetLibrary(true); }} className="inline-flex w-full items-center justify-center border border-gray-200 dark:border-gray-700 bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-full px-4 h-10 text-sm transition-colors">
                          <ImageIcon className="w-4 h-4 mr-0.5" />
                          Add image elements
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Voice & Script */}
                  <div className="rounded-2xl p-4 sm:p-6 shadow-sm mb-6 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-semibold flex items-center justify-center">3</span>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">What will they say?</h2>
                    </div>

                    {/* Voice Selection Row */}
                    <div className="grid grid-cols-2 gap-2 mb-4 min-h-[44px] sm:flex sm:flex-wrap sm:items-center">
                      {(isSyncingVoices || (loadingVoices && accumulatedVoices.length === 0)) ? (
                        <div className="col-span-2 flex items-center gap-3 px-4 py-2">
                          <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                          <span className="text-sm text-gray-500 dark:text-gray-400">Loading voices...</span>
                        </div>
                      ) : (
                        <>
                          {visibleVoices?.length > 0 ? (
                            visibleVoices.map((voice) => {
                              const displayName = voice.name.length > 12
                                ? voice.name.substring(0, 10) + '..'
                                : voice.name;

                              return (
                                <div
                                  key={voice.voiceId || voice.id}
                                  onClick={() => {
                                    handleVoiceSelection(voice.voiceId || voice.id, voice)
                                    setPreviewingVoiceId(null)
                                  }}
                                  className={`flex w-full min-w-0 items-center gap-1.5 px-2 py-1.5 sm:w-auto sm:gap-2 sm:px-3 sm:py-2 rounded-full border transition-all cursor-pointer ${selectedVoice === (voice.voiceId || voice.id)
                                    ? "border-orange-500 bg-orange-50 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400"
                                    : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600"
                                    }`}
                                >
                                  <button
                                    onClick={(e) => handlePlayVoice(voice.voiceId || voice.id, voice.previewUrl, e)}
                                    className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center transition-all ${previewingVoiceId === (voice.voiceId || voice.id)
                                      ? "bg-orange-500 text-white animate-pulse"
                                      : selectedVoice === (voice.voiceId || voice.id)
                                        ? "bg-orange-200 dark:bg-orange-500/30 hover:bg-orange-300 dark:hover:bg-orange-500/50"
                                        : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                      }`}
                                  >
                                    {previewingVoiceId === (voice.voiceId || voice.id) ? (
                                      <Pause className="w-3 h-3" />
                                    ) : (
                                      <Play className="w-3 h-3 ml-0.5" />
                                    )}
                                  </button>
                                  <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-1.5">
                                    <span className="block min-w-0 truncate text-xs sm:text-sm font-medium" title={voice.name}>{displayName}</span>
                                    <span className="block truncate text-[10px] text-gray-400 dark:text-gray-500 sm:hidden">{voice.accent}</span>
                                    <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">- {voice.accent}</span>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <p className="col-span-2 text-sm text-gray-500 dark:text-gray-400 px-4 py-2">
                              No matching voices found. Try another character or language.
                            </p>
                          )}
                          <button
                            onClick={() => setShowVoiceLibrary(true)}
                            className="col-span-2 flex items-center gap-1 justify-self-start px-3 py-2 text-sm font-medium text-orange-500 hover:text-orange-600"
                          >
                            View all voices
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Voice Controls */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <Menu as="div" className="relative">
                        <Menu.Button className="inline-flex items-center justify-center border border-gray-200 dark:border-gray-700 bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-3 h-9 text-xs rounded-full">
                          <Brackets className="w-4 h-4 mr-2" />
                          Insert Voice Cues
                          <ChevronDown className="w-4 h-4 ml-2" />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-0 mt-2 w-56 origin-top-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-50 p-1 max-h-60 overflow-y-auto">
                            {voiceCues.map((cue) => (
                              <Menu.Item key={cue.value}>
                                {({ active }) => (
                                  <button
                                    onClick={() => insertVoiceCue(cue.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 text-gray-700 dark:text-gray-300 ${active ? "bg-gray-50 dark:bg-gray-700" : ""}`}
                                  >
                                    {cue.icon} {cue.label}
                                  </button>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </Menu>



                      <button
                        ref={voiceSettingsBtnRef}
                        onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                        className={`h-9 px-4 rounded-full border text-sm font-medium flex items-center gap-2 transition-all ${showVoiceSettings ? "bg-orange-50 dark:bg-orange-500/20 border-orange-500 text-orange-600 dark:text-orange-400" : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"}`}
                      >
                        <Settings className="w-4 h-4" /> Voice Settings
                      </button>
                    </div>

                    {showVoiceSettings && (
                      <div ref={voiceSettingsRef} className="mb-4 min-w-0 p-3 sm:p-4 rounded-xl space-y-4 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">

                        <div>
                          <div className="mb-2 text-left">
                            <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delivery Style</label>
                          </div>
                          <VoiceStabilityControl
                            value={stability[0]}
                            onChange={(value) => setStability([value])}
                          />
                        </div>
                      </div>
                    )}

                    {/* Script Generation Controls */}
                    <div className="mb-3 min-w-0">
                      <div className="flex w-full flex-wrap items-center gap-2">
                        <button
                          onClick={handleGenerateScript}
                          disabled={isGeneratingScript}
                          className="inline-flex w-full sm:w-auto min-h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                        >
                          {isGeneratingScript ? (
                            <>
                              <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 shrink-0" />
                              Generate new script
                            </>
                          )}
                        </button>

                        <button
                          disabled
                          className={`flex w-full sm:w-auto min-h-10 items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all opacity-50 cursor-not-allowed ${showScriptPrompt
                            ? "bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400"
                            : "text-gray-500 dark:text-gray-400"
                            }`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${showScriptPrompt ? "rotate-180" : ""}`} />
                          Add instructions
                        </button>

                        <ScriptHistoryControls
                          currentScriptNumber={currentScriptNumber}
                          scriptCount={scriptCount}
                          goToPreviousScript={goToPreviousScript}
                          goToNextScript={goToNextScript}
                          disabled={isGeneratingScript}
                        />
                      </div>
                    </div>

                    {/* Optional Script Instructions Input */}
                    {showScriptPrompt && (
                      <div className="mb-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                        <label className="text-xs font-medium mb-2 block text-gray-500 dark:text-gray-400">
                          Additional instructions <span className="text-gray-400 dark:text-gray-500">(optional)</span>
                        </label>
                        <div className="relative">
                          <PenLine className="absolute left-3.5 top-3 w-5 h-5 text-gray-400 dark:text-gray-500" />
                          <textarea
                            ref={instructionsTextareaRef}
                            value={scriptPrompt}
                            onChange={(e) => {
                              setScriptPrompt(e.target.value)
                              e.target.style.height = "auto"
                              e.target.style.height = Math.max(72, e.target.scrollHeight) + "px"
                            }}
                            placeholder="E.g., Focus on pricing, highlight AI features, keep it casual… or leave empty to generate a new concept"
                            className="w-full min-h-[72px] max-h-[150px] overflow-y-auto hide-scrollbar pl-11 pr-4 py-2.5 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-1 focus:ring-orange-500 resize-y bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                          />
                        </div>
                      </div>
                    )}

                    {/* Script Textarea */}
                    <textarea
                      ref={scriptTextareaRef}
                      value={script}
                      onChange={(e) => {
                        setScript(e.target.value)
                        setCursorPosition(e.target.selectionStart)

                      }}
                      onClick={(e) => setCursorPosition(e.currentTarget.selectionStart)}
                      placeholder="Write your script here..."
                      className="w-full min-h-[120px] rounded-xl resize-y text-sm leading-relaxed p-4 focus:border-orange-300 focus:ring-orange-100 focus:outline-none border mb-4 overflow-y-auto hide-scrollbar bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder:text-gray-500"
                    />

                    {/* Voice Generation Controls */}
                    <div className="flex flex-wrap items-center gap-4">
                      {/* Word count removed from here - now in header */}

                      {/* Word and estimated duration */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm whitespace-nowrap text-gray-400 dark:text-gray-500">
                          {script.trim().split(/\s+/).filter(Boolean).length} words
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span className="text-sm whitespace-nowrap text-gray-400 dark:text-gray-500">
                          ~{Math.ceil(script.trim().split(/\s+/).filter(Boolean).length / 2.5)} sec
                        </span>
                      </div>

                      {/* Generate speech Button */}
                      <button
                        type="button"
                        onClick={generateAudio}
                        disabled={audioInProcess} aria-busy={audioInProcess}
                        className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 h-10 shrink-0 text-sm font-medium disabled:opacity-50"
                      >
                        {audioInProcess ? <Loader2 aria-hidden="true" className="w-4 h-4 mr-0.5 animate-spin motion-reduce:animate-none" /> : <RefreshCw aria-hidden="true" className="w-4 h-4 mr-0.5" />}
                        Generate speech
                      </button>
                      {(audioUrl || audioInProcess) && (
                        <SpeechWaveformPlayer
                          audioUrl={audioUrl}
                          audioBlob={audioBlob}
                          audioRef={audioRefGen}
                          isGenerating={audioInProcess}
                          onBeforePlay={() => audioRef.current.pause()}
                        />
                      )}
                    </div>

                    {/* Voice History Panel */}
                    {showHistory && voiceGenerations.length > 0 && (
                      <div className="mt-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Voice Generation History</h4>
                          <button onClick={() => setShowHistory(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {voiceGenerations.map((gen, index) => (
                            <button
                              key={gen.id}
                              onClick={() => loadGeneration(index)}
                              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${currentGenerationIndex === index
                                ? "bg-orange-100 dark:bg-orange-500/20 border border-orange-200 dark:border-orange-500/50"
                                : "bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600"
                                }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${currentGenerationIndex === index ? "bg-orange-500 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                                }`}>
                                <Play className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className={`text-sm font-medium ${currentGenerationIndex === index ? "text-gray-900 dark:text-orange-300" : "text-gray-900 dark:text-white"}`}>{gen.voiceName}</span>
                                  <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {gen.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <p className={`text-xs truncate ${currentGenerationIndex === index ? "text-gray-600 dark:text-orange-200/70" : "text-gray-500 dark:text-gray-400"}`}>{gen.script}</p>
                              </div>
                              {currentGenerationIndex === index && (
                                <Check className="w-4 h-4 text-orange-500 shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Background Media Upload Section */}
                  <div className="mt-8 p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#111111] shadow-sm">
                    <h3 className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6 px-1">
                      Background Video / Image
                    </h3>

                    <div className="flex flex-wrap gap-4 items-start">
                      {/* Add Media Button */}
                      {!backgroundMedia.backgroundMediaUrl && !backgroundMedia.isUploading && (
                        <div
                          onClick={() => { setAssetLibraryMode('background'); setShowAssetLibrary(true); }}
                          className="group flex flex-col items-center justify-center w-[76px] h-[92px] border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer hover:border-orange-500 dark:hover:border-orange-500 bg-white dark:bg-[#161616] transition-all hover:shadow-md"
                        >
                          <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center mb-2 group-hover:bg-orange-50 dark:group-hover:bg-orange-950/30 transition-colors">
                            <div className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center group-hover:border-orange-400 transition-colors">
                              <Plus className="h-3 w-3 text-gray-400 group-hover:text-orange-500" strokeWidth={3} />
                            </div>
                          </div>
                          <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400 group-hover:text-orange-500">
                            Add
                          </span>
                        </div>
                      )}

                      {/* Uploading State */}
                      {backgroundMedia.isUploading && (
                        <div className="flex flex-col items-center justify-center w-[76px] h-[92px] border border-orange-200 dark:border-orange-900/50 rounded-xl bg-orange-50/30 dark:bg-orange-950/10">
                          <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                          <span className="text-[10px] font-medium text-orange-500 mt-2">Uploading</span>
                        </div>
                      )}

                      {/* Media Preview Card */}
                      {backgroundMedia.backgroundMediaUrl && (
                        <div className="relative group w-[120px] h-[160px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                          {backgroundMedia.type === "video" ? (
                            <video
                              src={backgroundMedia.preview || backgroundMedia.backgroundMediaUrl}
                              className="w-full h-full object-cover"
                              muted
                              loop
                              onMouseOver={(e) => e.target.play()}
                              onMouseOut={(e) => e.target.pause()}
                            />
                          ) : (
                            <img
                              src={backgroundMedia.preview || backgroundMedia.backgroundMediaUrl}
                              alt="Background preview"
                              className="w-full h-full object-cover"
                            />
                          )}

                          {/* Remove Button Layer */}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button
                              onClick={() => setBackgroundMedia({ backgroundMediaUrl: null, type: null, preview: null, isUploading: false })}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-transform hover:scale-110"
                              title="Remove media"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Type Indicator */}
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/60 backdrop-blur-md rounded-md">
                            {backgroundMedia.type === "video" ? (
                              <Video className="h-3 w-3 text-white" />
                            ) : (
                              <Upload className="h-3 w-3 text-white" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Generate Video Button */}
                  <div className="flex items-center justify-between mt-8">
                    <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                          <Sparkles className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm font-medium">
                          {(() => {
                            const words = script.trim().split(/\s+/).filter(Boolean).length;
                            const estimatedDuration = Math.ceil(words / 2.5);
                            return words > 0 ? (estimatedDuration > 60 ? 750 : 500) : 0;
                          })()} credits
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-600">
                        (~{Math.ceil(script.trim().split(/\s+/).filter(Boolean).length / 2.5)} sec video)
                      </span>
                    </div>
                    <button
                      onClick={generateVideoAd}
                      disabled={loadingVideo || isProcessing || audioInProcess || backgroundMedia.isUploading || !audioUrl || !backgroundMedia.backgroundMediaUrl}
                      className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 h-12 text-base font-medium shadow-lg shadow-orange-200 dark:shadow-orange-500/20 disabled:opacity-50"
                    >
                      {loadingVideo ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-1.5" />}
                      {loadingVideo ? "Generating..." : "Create video"}
                    </button>
                  </div>
                  <p className="ai-disclaimer mt-6">
                    <span className="ai-disclaimer__lead">AI can make mistakes.</span>{" "}
                    Review your content before publishing. Claims, personal experiences, and testimonials must be genuine and accurate.
                  </p>
                </div>
              </div>

              {/* Right Panel - Preview */}
              <div className="talking-head-preview w-[380px] shrink-0 bg-white dark:bg-[#111111] border-l border-gray-100 dark:border-gray-800">
                <div className="p-6">
                  {/* Preview Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Preview character</h3>
                    <button
                      onClick={handleShowHistory}
                      className="flex items-center gap-1.5 text-sm font-medium text-orange-500 px-2 py-1 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Previous generations
                    </button>
                  </div>

                  {/* Preview Card */}
                  <div
                    className={`rounded-2xl overflow-hidden mb-6 relative w-full aspect-[9/16] flex items-center justify-center ${
                      isProcessing ? "bg-white dark:bg-white/5" : "bg-gray-100 dark:bg-gray-800"
                    }`}
                  >
                    {currentChar ? (
                      <>
                        {isProcessing ? (
                          <div className="absolute inset-0 animate-pulse bg-gray-100 dark:bg-white/5">
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                              <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin"></div>
                              <p className="text-xs font-medium animate-pulse text-orange-500 dark:text-orange-400">Generating Character...</p>
                            </div>
                          </div>
                        ) : (
                          <>
                            {characterImage || currentChar?.baseImage?.originalUrl ? (
                              <img
                                src={characterImage || currentChar?.baseImage?.originalUrl}
                                alt="Character preview"
                                className="w-full h-full object-cover absolute inset-0"
                              />
                            ) : (
                              <div className="text-gray-500 dark:text-gray-400 text-sm font-medium z-10 text-center px-4">
                                Select a character to preview
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400 text-sm font-medium z-10 text-center px-4">
                        Select a character to preview
                      </div>
                    )}

                    {/* Overlay */}
                    <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
                      <span className="bg-orange-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        Character
                      </span>
                      <span className="bg-gray-900/80 text-white text-xs font-medium px-2 py-1 rounded-full">
                        9:16
                      </span>
                    </div>

                    {/* Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-white/80 text-xs leading-relaxed">
                        Preview shows character with applied customizations (clothes, environment, pose, props from Step 2)
                      </p>
                    </div>
                  </div>

                  {/* Tips Section - Collapsible */}
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <Disclosure.Button className={`w-full p-4 ${open ? "rounded-t-xl" : "rounded-xl"} bg-orange-50 dark:bg-orange-950/50 border border-orange-100 dark:border-orange-900/50 hover:bg-orange-100/70 dark:hover:bg-orange-950/70 transition-colors flex items-center justify-between`}>
                          <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-orange-500" />
                            <h4 className="font-semibold text-sm text-gray-900 dark:text-orange-200">How to Make Your Videos Feel Real</h4>
                          </div>
                          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""} text-orange-500 dark:text-orange-300`} />
                        </Disclosure.Button>
                        <Transition
                          enter="transition duration-100 ease-out"
                          enterFrom="transform scale-95 opacity-0"
                          enterTo="transform scale-100 opacity-100"
                          leave="transition duration-75 ease-out"
                          leaveFrom="transform scale-100 opacity-100"
                          leaveTo="transform scale-95 opacity-0"
                        >
                          <Disclosure.Panel className="px-4 pb-4 pt-4 bg-orange-50 dark:bg-orange-950/50 border border-t-0 border-orange-100 dark:border-orange-900/50 rounded-b-xl">
                            <div className="space-y-5 text-xs text-gray-600 dark:text-orange-200/80">

                              <p className="text-sm text-gray-700 dark:text-orange-200">These tips help your AI videos sound and feel more natural, believable, and human.</p>

                              <p className="text-xs italic text-gray-500 dark:text-orange-300">Keep scripts under 60 seconds for best engagement</p>

                              {/* Natural Speech */}
                              <div>
                                <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Natural Speech</h5>
                                <ul className="space-y-1.5">
                                  <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>Write the way people actually talk — avoid formal marketing language</span></li>
                                  <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>Keep sentences short and conversational</span></li>
                                  <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>{"Avoid overusing [pause] or … — it can feel unnatural"}</span></li>
                                </ul>
                                <div className="mt-3 p-2.5 rounded-lg space-y-1.5 bg-white dark:bg-gray-900/50">
                                  <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-medium">Instead of:</span> This revolutionary platform dramatically improves productivity.</p>
                                  <p className="text-xs text-gray-700 dark:text-gray-300"><span className="font-medium text-orange-500">Try:</span> I tried this and honestly it worked way better than I expected.</p>
                                </div>
                              </div>

                              {/* Natural Pacing */}
                              <div>
                                <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Natural Pacing</h5>
                                <ul className="space-y-1.5">
                                  <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>Slightly faster speech sounds more natural — real people talk quickly</span></li>
                                  <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>{"Use [fast] or [faster] for casual, excited delivery"}</span></li>
                                  <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span> <span>{"Use [slow] only for important emphasis"}</span></li>
                                </ul>
                              </div>

                              {/* Voice Cues */}
                              <div>
                                <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Voice Cues</h5>
                                <div className="p-2.5 rounded-lg grid grid-cols-2 gap-x-4 gap-y-1 bg-white dark:bg-gray-900/50">
                                  <p className="text-xs"><span className="font-mono text-orange-500">[fast]</span> faster delivery</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[faster]</span> very quick, excited</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[slow]</span> slower emphasis</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[pause]</span> natural break</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[laugh]</span> light laugh</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[chuckle]</span> soft laugh</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[giggles]</span> playful laughter</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[sigh]</span> relief/frustration</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[whisper]</span> quieter tone</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[breath]</span> breathing pause</p>
                                  <p className="text-xs"><span className="font-mono text-orange-500">[emphasis]</span> stress words</p>
                                </div>
                                <p className="text-xs mt-2 italic text-gray-500 dark:text-gray-400">{"Example: I almost didn't try this… [chuckle] but I'm glad I did."}</p>
                              </div>

                              {/* Emotion & Punctuation */}
                              <div>
                                <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Emotion & Punctuation</h5>
                                <ul className="space-y-1">
                                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> <span><strong>!</strong> adds excitement</span></li>
                                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> <span><strong>?</strong> creates curiosity</span></li>
                                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> <span><strong>…</strong> creates a thinking pause</span></li>
                                </ul>
                              </div>

                              {/* Setting & Style */}
                              <div>
                                <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Setting & Style</h5>
                                <p className="text-xs mb-2 text-gray-400 dark:text-orange-300/60">Casual settings feel more authentic. Good options:</p>
                                <ul className="space-y-1">
                                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> home office, kitchen, living room</li>
                                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> natural window light or soft indoor lighting</li>
                                  <li className="flex items-center gap-2"><span className="text-orange-500">•</span> talk like sharing with a friend, not reading a script</li>
                                </ul>
                              </div>

                              {/* Natural Hooks */}
                              <div>
                                <h5 className="font-semibold mb-2 text-sm text-gray-800 dark:text-orange-200">Natural Hooks</h5>
                                <p className="text-xs mb-2 text-gray-400 dark:text-orange-300/60">Casual openers that grab attention:</p>
                                <div className="p-2.5 rounded-lg bg-white dark:bg-gray-900/50">
                                  <p className="text-xs italic text-gray-600 dark:text-gray-300">{"\"I didn't expect this to work…\" • \"I wish I knew this earlier.\" • \"Nobody talks about this.\""}</p>
                                </div>
                              </div>

                            </div>
                          </Disclosure.Panel>
                        </Transition>
                      </>
                    )}
                  </Disclosure>
                </div>
              </div>
            </div>
          </div>

          {/* ✅ Step 1 — Render memoized sidebar */}
          {MemoSidebar}

          {/* Overlay when sidebar is open */}
          {showVoiceLibrary && (
            <div
              className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40"
              onClick={() => setShowVoiceLibrary(false)}
            />
          )}

          {/* Generation History Sidebar */}
          {showGenerationHistory && (
            <GenerationHistorySidebar
              showGenerationHistory={showGenerationHistory}
              setShowGenerationHistory={setShowGenerationHistory}
              generatedImages={historyImages}
              selectedGenerationImage={selectedGenerationImage}
              setSelectedGenerationImage={setHistoryCharacter}
              loading={isLoadingHistory}
              setCharacterImage={setCharacterImage}
            />
          )}

          {/* Overlay when generation history is open */}
          {showGenerationHistory && (
            <div
              className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40"
              onClick={() => setShowGenerationHistory(false)}
            />
          )}
        </>
      )
      }
      {/* Asset Library Modal */}
      <AdAssetLibraryModal
        isOpen={showAssetLibrary}
        onClose={() => setShowAssetLibrary(false)}
        onSelect={handleSelectFromAssetLibrary}
        allowedTypes={assetLibraryMode === 'background' ? ['image', 'video'] : ['image']}
      />
    </div>
  );
}
